import { logError } from '@zg-rentals/log-error';

/**
 * @file ported from:
 * https://bitbucket.hotterpads.com/projects/rp/repos/rentals-js/browse/modules/zrs-comet/src/components/Footer/components/cookie-pref.jsx?at=976564c2c5b624c22043bc3b336960362b839283
 * Since DataGrail is replacing onetrust, all of this code will deprecated in a few months.
 * Therefore, I am not going to optimize much. New code is prepended with a comment "NEW".
 * This module is mainly for apps that weren't getting OneTrust because they didn't use
 * a random components that provided it. This is a fallback, used in our rentals-analytics module.
 */

const OPTANON_SCRIPT_ELEMENT_ID = 'optanon-script';
const ENTER_KEY_ID = 13;
const ONETRUST_CLASS_AND_ID_NAME = 'cookie-preference';
const ONETRUST_SCRIPTS_BY_BRAND = {
  ZILLOW: {
    TEST: '25d8097d-9a5e-41c8-ab04-0114f1e8ff0b-test',
    PROD: '25d8097d-9a5e-41c8-ab04-0114f1e8ff0b',
  },
  HOTPADS: {
    TEST: '95d81fc6-f1a6-48cb-848f-fdb5978c5149-test',
    PROD: '95d81fc6-f1a6-48cb-848f-fdb5978c5149',
  },
};

const getOTDomainFromHostname = () => {
  const hostname = window.location.hostname;

  if (hostname === 'www.zillow.com') {
    return ONETRUST_SCRIPTS_BY_BRAND.ZILLOW.PROD;
  }

  if (hostname === 'hotpads.com') {
    return ONETRUST_SCRIPTS_BY_BRAND.HOTPADS.PROD;
  }

  // default to zillow for test (for non-prod) as it doesn't matter which you use
  return ONETRUST_SCRIPTS_BY_BRAND.ZILLOW.TEST;
};

const loadOneTrustJSScript = () => {
  // check to ensure the script is not already loaded (great for migrating to this new dedicated version)
  const optanonSetUpScript = document.createElement('script');
  optanonSetUpScript.id = OPTANON_SCRIPT_ELEMENT_ID;
  const dataDomainScriptAttr = document.createAttribute('data-domain-script');
  dataDomainScriptAttr.value = getOTDomainFromHostname();
  optanonSetUpScript.setAttributeNode(dataDomainScriptAttr);
  optanonSetUpScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
  optanonSetUpScript.type = 'text/javascript';
  document.body.appendChild(optanonSetUpScript);
};

const toggleOrSetupOneTrust = () => {
  // @ts-ignore
  if (typeof Optanon === 'undefined') {
    loadOneTrustJSScript();
  } else {
    // @ts-ignore
    Optanon.ToggleInfoDisplay();
  }
};

// @ts-ignore
const setupOneTrustEventListeners = (cookiePreferenceElement) => {
  if (cookiePreferenceElement) {
    cookiePreferenceElement.addEventListener('click', toggleOrSetupOneTrust);
  }

  if (cookiePreferenceElement && !cookiePreferenceElement.keydown) {
    // @ts-ignore
    cookiePreferenceElement.onkeydown = function (event) {
      if (event.keyCode === ENTER_KEY_ID) {
        toggleOrSetupOneTrust();
      }
    };
  }
};

const fetchCookiePreferenceElementsByClassName = () => {
  const cookiePreferenceClassElements: Array<Element> = [];
  const cookiePrefElements = document.getElementsByClassName(ONETRUST_CLASS_AND_ID_NAME);
  for (let i = 0; i < cookiePrefElements.length; i++) {
    cookiePreferenceClassElements.push(cookiePrefElements[i]);
  }
  return cookiePreferenceClassElements;
};

// NEW: some apps take a long time to load the footer
// this feature was broken in some apps because of this
const waitForFooterElements = (callback: () => void): void => {
  let attempts = 0;
  // this is only to add the callback after the footer elements
  const maxAttempts = 10; // 10 seconds max (10 * 1 second)

  const poll = () => {
    const elements = document.querySelectorAll(`#${ONETRUST_CLASS_AND_ID_NAME}, .${ONETRUST_CLASS_AND_ID_NAME}`);

    if (elements.length > 0) {
      callback();
      return;
    }
    if (attempts >= maxAttempts) {
      return;
    }

    attempts++;
    setTimeout(poll, 1_000);
  };

  poll();
};

export const setupOneTrustFooter = () => {
  // NEW: Nothing here should crash the app
  try {
    // NEW: add this check here so if there are other implementations in effect
    // it won't trigger anything twice
    if (document.getElementById(OPTANON_SCRIPT_ELEMENT_ID)) {
      return;
    }

    // Always load the script immediately
    loadOneTrustJSScript();

    // NEW: Wait for footer elements before setting up event listeners
    // OK to poll every second since this is only adding an event listener to footer element
    // Also, not all apps have footers... Like satellite apps
    waitForFooterElements(() => {
      // backward compatibility with previous implementations, attach listeners to links with id as
      // cookie-preference
      const cookiePreferenceElement = document.getElementById(ONETRUST_CLASS_AND_ID_NAME);

      if (cookiePreferenceElement) {
        setupOneTrustEventListeners(cookiePreferenceElement);
      }

      const cookiePreferenceClassElements = fetchCookiePreferenceElementsByClassName();
      cookiePreferenceClassElements.map((cookiePrefElement) => {
        setupOneTrustEventListeners(cookiePrefElement);
      });
    });
  } catch (error) {
    logError({ error, errorType: 'OneTrustError' });
  }
};
